import React from "react";
import PropTypes from "prop-types";
import { COLORS } from "constant/adminTheme";

/**
 * Tiny 後臺的頁面層級 tab 導覽（公版）
 *
 * 位置固定在 `TinyPageHeader` 下方、內容上方，自成一列。
 *
 * # 它負責什麼
 *
 * **切換「同一份資料的不同視角」** —— 期數明細／趨勢總覽、總覽／按俱樂部／
 * 按超代、週趨勢／機器人明細／獎池。
 *
 * 🚫 **不負責換「哪一份資料」** —— 那是 `TinyPageHeader` 的 `scope`
 * （期間／週期選擇器）。判準：它換掉的是哪一份資料 → scope；
 * 同一份資料的哪個視角 → tab。
 *
 * # 為什麼要抽出來
 *
 * 抽之前實測：15 個頁面用 reactstrap `Nav`，active 樣式**四種都不一樣**
 *（俱樂部管理／遊戲統計 `#005580→#007399`、EcoBot 藍、自營Bot 綠、
 * 金流數據 `116,132,255`），而且 `EnhancedNavLink` 在
 * `TinyClubConfig.js` 與 `TinyGameStats.js` **各有一份複製**。
 *
 * 那兩份目前值還一樣，但這個 repo 已經因為「靠複製維持一致性」漂過四次
 *（`getWeekExpenses`、`CashflowEditCell`、下載檔案、以及這批 header）——
 * 每一次都是要改的時候才發現有兩份。
 *
 * # active 的底色：**單色，不用漸層**
 *
 * 取 `adminTheme.accent`（#667eea）本身。
 *
 * 🚫 **不要用 `accentGradient`**（#667eea → #764ba2）—— 那是為大面積的
 * 按鈕/卡片設計的，放在只有 30 幾 px 高的 tab 上，淺紫到深紫的跨度會在
 * 一顆小色塊裡整個走完，看起來很躁。tab 是導覽不是主角，底色安靜就好。
 *
 * ⚠️ 俱樂部管理頁原本自己就內部不一致（tab 藍綠、按鈕紫），收斂到 accent
 * 之後整頁才會是一致的。是不是要維持紫色另議，但**不論換成什麼色，
 * 這裡都維持單色**。
 *
 * 🚫 **刻意不開放自訂顏色。** 開了就會退化成「每頁自己配色」，
 * 也就是現在這個狀態。同頁多組並存的控制項（如金流數據頁的幣別／粒度／
 * 單位）是另一回事，那邊用不同 accent 幫助視線對位是刻意的設計，不走這支。
 */

/**
 * `look`：
 *   underline  底線式（預設）—— 文字 + 共用底線，active 加一條 accent 底線。
 *              坊間後台的主流（GitHub / Material / Ant Design / Atlassian）。
 *              🔑 靠那條共用底線把導覽錨在內容上方，所以**不需要外框**，
 *                 也就不用把頁面內容包成一個大面板。
 *   pill       膠囊 —— 適合 2~3 個互斥模式（iOS segmented control 那種）。
 *              ⚠️ 用在 5 個分頁上會讀起來像 switch 不像導覽。
 */
// ⚠️ 評估期：--tph-nav-active 由畫面上的選色開關寫進 :root，選定後移除，
//    顏色就固定回 adminTheme.navActive。
const NAV = "var(--tph-nav-active, " + COLORS.navActive + ")";

/* active tab 的底色 —— 深色實底 #20293a（Brad 2026-09-10 從六個候選中選定）。
   起因是「每週交收總覽」自己刻的那組 tab（白字 + 底線 + 一層底色）比公版舒服，
   所以公版補上背景，但**文字仍是白色、active 指示仍是那條底線**，只多背景。

   🔑 選深色實底而不是 accent 淡染：它跟 active 底線（亮青）是兩個不同的訊號 ——
      底線負責「選中的是這個」，底色只負責把選中的那格從共用底線上「抬起來」。
      同色淡染會讓兩個訊號疊在一起、反而讓底線變不明顯。
   ⚠️ 它比卡片底（#1e2530）**亮一點點**，所以在卡片內（bare）與獨立列上都看得出來；
      再深就會在深色頁面上消失，再亮就會搶過內容。 */
const NAV_BG = "#20293a";

/* pill 的 active 底色 —— 深青 #0e7490（v2.45.0，Brad 2026-09-25）。
   原本直接用 NAV（亮青 #11cdef）當實底配白字，對比只有約 1.8:1，
   字跟底「卡在一起」看不清楚。取同色相的深一階，白字對比約 5.4:1，
   而且跟導覽的亮青仍是同一個色系。亮青光暈也一併拿掉（深底配亮光暈很躁）。 */
const PILL_BG = "#0e7490";

const TinyPageTabs = ({ items, active, onChange, bare = false, look = "underline", style, right = null }) => (
  <div
    role="tablist"
    className={`tph-tabs tph-tabs--${look}${bare ? "" : " tph-tabs--standalone"}${right ? " tph-tabs--has-right" : ""}`}
    style={{
      /* 🔴 **不要在這裡設 flexWrap / overflowX** —— inline style 永遠贏過
            CSS media query，`.tph-tabs` 在 767px 以下要改成橫向捲動的那條
            規則會整個失效（實測：EcoBot 5 個 tab 在 420px 下換行成兩排）。
            排列相關的都留給 .css，這裡只放不隨斷點變的東西。 */
      display: "flex",
      gap: look === "underline" ? 2 : 6,
      ...(look === "underline"
        ? {
            // 共用底線 —— 這條線就是「導覽屬於底下那塊內容」的視覺錨點
            padding: 0,
            borderBottom: `1px solid ${COLORS.border}`,
          }
        : {
            padding: 5,
            background: bare ? "transparent" : COLORS.cardBg,
            border: bare ? "none" : `1px solid ${COLORS.border}`,
            borderRadius: 10,
          }),
      ...style,
    }}
  >
    {(items || []).filter(Boolean).map((it) => {
      const isActive = it.key === active;
      return (
        <button
          key={it.key}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => !isActive && onChange && onChange(it.key)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 14,
            fontWeight: isActive ? 600 : 400,
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            cursor: isActive ? "default" : "pointer",
            border: "none",
            background: "transparent",
            transition: "color 0.16s ease, border-color 0.16s ease",
            color: isActive ? COLORS.textPrimary : COLORS.textMuted,
            ...(look === "underline"
              ? {
                  padding: "10px 16px",
                  // 用 2px 底線當 active 指示：導覽不該是畫面上最搶眼的東西
                  borderBottom: `2px solid ${isActive ? NAV : "transparent"}`,
                  marginBottom: -1,
                  // 評估中的 active 底色（見上面 NAV_BG）。上緣切圓角、下緣不切，
                  // 讓色塊看起來是「坐在」那條底線上而不是浮著。
                  background: isActive ? NAV_BG : "transparent",
                  borderRadius: "6px 6px 0 0",
                }
              : {
                  padding: "9px 16px",
                  borderRadius: 7,
                  color: isActive ? "#fff" : COLORS.textSecondary,
                  background: isActive ? PILL_BG : "transparent",
                }),
          }}
          onMouseEnter={(e) => {
            if (isActive) return;
            if (look === "underline") e.currentTarget.style.color = COLORS.textPrimary;
            else e.currentTarget.style.background = "rgba(255,255,255,0.06)";
          }}
          onMouseLeave={(e) => {
            if (isActive) return;
            if (look === "underline") e.currentTarget.style.color = COLORS.textMuted;
            else e.currentTarget.style.background = "transparent";
          }}
        >
          {it.icon && <i className={it.icon} style={{ fontSize: 13, opacity: isActive ? 0.95 : 0.7 }} />}
          {it.label}
          {/* 次要計數（例如「按俱樂部 24」）。刻意只收數字，不收任意節點 */}
          {it.count !== undefined && it.count !== null && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "1px 7px",
                borderRadius: 999,
                background: isActive ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.09)",
                color: isActive ? "#fff" : COLORS.textMuted,
              }}
            >
              {it.count}
            </span>
          )}
        </button>
      );
    })}
    {/* 右側動作槽 —— 給「這組 tab 共用的動作」用（例如匯出 Excel）。
        🔑 它跟 tab 是同一列，所以不會多佔一行；`margin-left: auto` 推到最右。
        🚫 不要拿來放 filter —— filter 會換掉資料，動作不會。 */}
    {right ? <div className="tph-tabs__right">{right}</div> : null}
  </div>
);

TinyPageTabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      /** 圖示的**完整 className**（例如 "fa fa-th-list"），這裡不自動補前綴。
       *  🚫 不要用 `nc-icon nc-*`（那套字體沒有 glyph class，一律空白）、
       *     也不要用 FA4 的 `-o` 尾綴或 brands 圖示配 `fa` 前綴 —— 同樣是空白。
       *  ⚠️ 每個 tab 都要給圖示（Brad 2026-09-10）。同質清單（例如盲注級別）
       *     全部用同一個圖示，不要一格配一個。 */
      icon: PropTypes.string,
      count: PropTypes.number,
    })
  ).isRequired,
  active: PropTypes.string,
  onChange: PropTypes.func,
  /** true = 收在 header 卡片內（不畫自己的外框）；false = 自成一張卡 */
  bare: PropTypes.bool,
  /** underline（預設，坊間主流）/ pill（只適合 2~3 個互斥模式） */
  look: PropTypes.oneOf(["underline", "pill"]),
  /** 靠最右邊的動作（例如匯出 Excel），跟 tab 同一列 */
  right: PropTypes.node,
  style: PropTypes.object,
};

export default TinyPageTabs;
